import { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { isEmpty, reduce } from 'lodash'

import routes from '../../routers'
import { createRecipe, getRecipeById } from '../../api/recipe'
import useDirty from '../../utils/hooks/useDirty'
import { setLoading } from '../../redux/actions/ui'
import { ACTIVE_STATUS, DEFAULT_VALUES_ADD_RECIPE_SECOND_STEP } from '../../utils/constants'
import IngredientsItem from '../../components/IngredientsItem'
import InputSimple from '../../components/InputSimple'
import ButtonCustom from '../../components/ButtonCustom'
import FieldFeedback from '../../components/FieldFeedback'
import {
  RECIPE_LIMITS,
  maxLengthCheck,
  scrollToFirstProblem,
  showServerErrors,
} from '../../utils/recipeLimits'
import LayoutRecipe from './components/LayoutRecipe'

import './style.css'

const AddRecipe = () => {
  const { filterDirtyFields } = useDirty()
  const dispatch = useDispatch()
  const navigation = useNavigate()
  const [required, setRequired] = useState(true)
  const [to_publish, setToPublish] = useState(false)
  const [disableButton, setDisableButton] = useState(false)
  const { control, handleSubmit, reset, setValue, setError, formState } = useForm()
  const { errors } = formState
  // Problems the API found that aren't next to a field on this page
  const [problems, setProblems] = useState([])
  const [draftSaved, setDraftSaved] = useState(false)
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ingredients',
  })
  const [idRecipe] = useSearchParams({})
  const idRecipeForm = idRecipe.get('id')

  const addNewIngredient = () => {
    append({
      quantity: '',
      measurement: '',
      name: '',
    })
  }

  const onSubmit = (data) => {
    const newData = filterDirtyFields({
      dirtyFields: { ...data, measurement: data?.measurement },
      data,
      current_step: 2,
      urlParams: idRecipeForm,
    })

    setDisableButton(true)
    setProblems([])
    createRecipe({
      ...newData,
      measurement: data?.measurement,
      amount: data?.amount,
      to_publish,
    })
      .then(({ data }) => {
        if (required) {
          return navigation(`${routes.addRecipeThird}?id=${data?.id}`)
        }
        setDraftSaved(true)
      })
      .catch((e) => {
        setProblems(showServerErrors(e, setError, 2))
        scrollToFirstProblem()
      })
      .finally(() => setDisableButton(false))
  }

  useEffect(() => {
    if (!isEmpty(idRecipeForm)) {
      dispatch(setLoading(true))
      return getRecipeById(idRecipeForm)
        .then(({ data }) => {
          const filterDataRecipe = filterDirtyFields({
            dirtyFields: DEFAULT_VALUES_ADD_RECIPE_SECOND_STEP,
            data,
            checkArray: true,
          })
          setToPublish(data?.status === ACTIVE_STATUS)
          reset({ ...DEFAULT_VALUES_ADD_RECIPE_SECOND_STEP, ...filterDataRecipe })
          return reduce(
            filterDataRecipe,
            (res, value, key) => {
              setValue(key, value, { shouldDirty: true })
            },
            {}
          )
        })
        .catch(() => navigation(routes.notFound))
        .finally(() => dispatch(setLoading(false)))
    }
    navigation(routes.notFound)
  }, [])

  return (
    <LayoutRecipe
      step={2}
      navigationPrev={`${routes.addRecipe}?id=${idRecipeForm}`}
      navigationNext
      onSubmit={handleSubmit(onSubmit, scrollToFirstProblem)}
      problems={problems}
      draftSaved={draftSaved}
      onDraftSavedClose={() => setDraftSaved(false)}
      disableButtonSave={disableButton}
      isLoading={disableButton}
      navigationPrevTitle="Next: steps"
      title="What goes in it?"
      subTitle="Add each ingredient with its amount. Add as many as it takes."
      setRequired={setRequired}
      onEnterInLastRow={addNewIngredient}
    >
      <section className="formSection">
        <h2 className="formSectionTitle">Ingredients</h2>
        <p className="formSectionHint">
          Amount, unit and ingredient, for example 2 · cups · flour.
        </p>
        <div className="ingredientRows" data-enter-adds-row>
          {fields.map((item, index) => {
            return (
              <IngredientsItem
                key={item.id}
                index={index}
                control={control}
                remove={remove}
                errors={errors}
              />
            )
          })}
        </div>
        <div className="recipeButtonWrap">
          <ButtonCustom
            onClick={addNewIngredient}
            type="button"
            style="recipeButtonAddIngredient"
            title="+ Add another ingredient"
          />
        </div>
      </section>
      <section className="formSection">
        <h2 className="formSectionTitle">How much does it make?</h2>
        <p className="formSectionHint">For example 12 cookies, 6 servings or 1 loaf.</p>
        <div className="ingredientsCount globalInnerIngredients">
          <div className="ingredientsCount">
            <Controller
              name="amount"
              control={control}
              rules={{
                validate: {
                  maxLength: maxLengthCheck('How much it makes', RECIPE_LIMITS.amount.max),
                },
              }}
              render={({ field, fieldState }) => (
                <InputSimple
                  {...field}
                  {...fieldState}
                  showErrorText={false}
                  nameInputPlaceholder="12"
                  styleInput="inputRecipeNumber textAlightCenter numberInputCustom"
                  className="ingredientsAmount"
                />
              )}
            />

            <div>
              <Controller
                name="measurement"
                control={control}
                rules={{
                  validate: {
                    maxLength: maxLengthCheck(
                      'The unit for how much it makes',
                      RECIPE_LIMITS.measurement.max
                    ),
                  },
                }}
                render={({ field, fieldState }) => (
                  <InputSimple
                    {...field}
                    {...fieldState}
                    showErrorText={false}
                    nameInputPlaceholder="cookies"
                    styleInput="inputRecipeNumber textAlightCenter numberInputCustom wrapDropDoze"
                    className="ingredientsMeasurement"
                  />
                )}
              />
            </div>
          </div>
        </div>
        <div data-field>
          <FieldFeedback error={errors.amount || errors.measurement} />
        </div>
      </section>
    </LayoutRecipe>
  )
}

export default AddRecipe
